import { useCallback, useReducer, useEffect, useMemo } from 'react'
import { menuOrItemsMatchSearch } from '@components/groupOrders/UniversalMenuPreview/utils'
import {
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  SearchUniversalPreviewMenusReq,
  UniversalMenuPreviewMenu,
  UniversalMenuPreviewFilters,
} from '@types'
import {
  computeItemAvailbility,
  computeMenuAvailability,
  decorateItem,
  groupByVendor,
} from '@components/groupOrders/UniversalMenuPreview/utils'

export interface UseGOMenuSearchProps {
  blacklistedVendorIds: Set<string>
  blacklistedItemIds: Set<string>
  hqId: number
  search: string
  searchGOMenus: (params: SearchUniversalPreviewMenusReq) => Promise<any>
  displayFailureMessage: (mssg: string) => void
}

export interface UseGOMenuSearchResult {
  loadingMenus: boolean
  menus: VendorGroup[]
  filters: UniversalMenuPreviewFilters
  resetFilters: () => void
  setEnableSides: (val: boolean) => void
  setEnableModifications: (val: boolean) => void
  setFlatPriceMarginMin: (val: number | null) => void
  setSubisdyType: (t: string) => void
  setSubsidyAmt: (n: number | null) => void
  setDayOfWeek: (n: number | null) => void
  setMaxPriceLimit: (n: number | null) => void
}

type Act =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_MENUS'; payload: UniversalMenuPreviewMenu[] }
  | { type: 'SET_ENABLE_SIDES'; payload: boolean }
  | { type: 'SET_ENABLE_MODS'; payload: boolean }
  | { type: 'SET_FLAT_PRICE_MARGIN_MIN'; payload: number | null }
  | { type: 'SET_SUBSIDY_TYPE'; payload: string | null }
  | { type: 'SET_SUBSIDY_AMT'; payload: number | null }
  | { type: 'SET_DAY_OF_WEEK'; payload: number | null }
  | { type: 'SET_MAX_PRICE_LIMIT'; payload: number | null }
  | { type: 'RESET_FILTERS' }

type State = {
  loadingMenus: boolean
  rawMenus: UniversalMenuPreviewMenu[]
  filters: UniversalMenuPreviewFilters
}

const initialFilters: UniversalMenuPreviewFilters = {
  enableSides: true,
  enableMods: true,
  flatPriceMarginMin: null,
  subsidyType: null,
  subsidyAmt: null,
  dayOfWeek: null,
  maxPriceLimit: null,
}

const initialState: State = {
  loadingMenus: false,
  rawMenus: [],
  filters: initialFilters,
}

const reducer = (s: State, a: Act): State => {
  switch (a.type) {
    case 'SET_LOADING':
      return { ...s, loadingMenus: a.payload }
    case 'SET_MENUS':
      return { ...s, rawMenus: a.payload ?? [] }
    case 'SET_FLAT_PRICE_MARGIN_MIN':
      return { ...s, filters: { ...s.filters, flatPriceMarginMin: a.payload } }
    case 'SET_ENABLE_SIDES':
      return { ...s, filters: { ...s.filters, enableSides: a.payload } }
    case 'SET_ENABLE_MODS':
      return { ...s, filters: { ...s.filters, enableMods: a.payload } }
    case 'SET_SUBSIDY_TYPE':
      return { ...s, filters: { ...s.filters, subsidyType: a.payload } }
    case 'SET_SUBSIDY_AMT':
      return { ...s, filters: { ...s.filters, subsidyAmt: a.payload } }
    case 'SET_DAY_OF_WEEK':
      return { ...s, filters: { ...s.filters, dayOfWeek: Number(a.payload) } }
    case 'SET_MAX_PRICE_LIMIT':
      return { ...s, filters: { ...s.filters, maxPriceLimit: a.payload } }
    case 'RESET_FILTERS':
      return { ...s, filters: { ...initialFilters } }
    default:
      return s
  }
}

export const useGOMenuSearch = ({
  blacklistedItemIds,
  blacklistedVendorIds,
  hqId,
  search,
  searchGOMenus,
  displayFailureMessage,
}: UseGOMenuSearchProps): UseGOMenuSearchResult => {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    let cancelled = false
    const fetchOnce = async () => {
      if (!hqId) {
        return
      }
      dispatch({ type: 'SET_LOADING', payload: true })
      try {
        const resp = await searchGOMenus({ hqId })
        const next: UniversalMenuPreviewMenu[] = Array.isArray(resp?.finalMenus)
          ? resp.finalMenus
          : resp ?? []
        if (!cancelled) {
          dispatch({ type: 'SET_MENUS', payload: next })
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      } catch (e: any) {
        if (!cancelled) {
          displayFailureMessage(`Error fetching menus: ${e?.message ?? e}`)
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }
    fetchOnce()

    return () => {
      cancelled = true
    }
  }, [hqId, searchGOMenus, displayFailureMessage])

  const setEnableSides = useCallback((val: boolean) => {
    dispatch({ type: 'SET_ENABLE_SIDES', payload: val })
  }, [])
  const setEnableModifications = useCallback((val: boolean) => {
    dispatch({ type: 'SET_ENABLE_MODS', payload: val })
  }, [])
  const setSubisdyType = useCallback((t: string) => {
    dispatch({ type: 'SET_SUBSIDY_TYPE', payload: t ?? null })
  }, [])
  const setSubsidyAmt = useCallback((n: number | null) => {
    dispatch({ type: 'SET_SUBSIDY_AMT', payload: n })
  }, [])
  const setFlatPriceMarginMin = useCallback((n: number | null) => {
    dispatch({ type: 'SET_FLAT_PRICE_MARGIN_MIN', payload: n })
  }, [])
  const setDayOfWeek = useCallback((n: number | null) => {
    dispatch({ type: 'SET_DAY_OF_WEEK', payload: n })
  }, [])
  const setMaxPriceLimit = useCallback((n: number | null) => {
    dispatch({ type: 'SET_MAX_PRICE_LIMIT', payload: n })
  }, [])

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' })
  }, [])

  const groupedMenus = useMemo(() => {
    const decoratedMenus: PreviewMenu[] = state.rawMenus.map((m) => {
      const decoratedItems: PreviewMenuItem[] = (m.allMenuItems ?? []).map(
        (it) => {
          const enriched = decorateItem(it, state.filters)
          const availability = computeItemAvailbility(
            enriched,
            blacklistedItemIds,
            state.filters,
          )

          return { ...enriched, availability }
        },
      )

      const availability = computeMenuAvailability(
        m,
        state.filters,
        blacklistedVendorIds,
      )

      return {
        ...m,
        allMenuItems: decoratedItems,
        availability,
      }
    })

    const searched = decoratedMenus.filter((m) =>
      menuOrItemsMatchSearch(
        m.chef.fullName,
        m.name,
        m.allMenuItems.map((i) => ({ name: i.name })),
        search,
      ),
    )

    return groupByVendor(searched)
  }, [
    state.rawMenus,
    state.filters,
    blacklistedVendorIds,
    blacklistedItemIds,
    search,
  ])

  return {
    loadingMenus: state.loadingMenus,
    menus: groupedMenus,
    filters: state.filters,

    resetFilters,
    setEnableSides,
    setEnableModifications,
    setFlatPriceMarginMin,
    setSubisdyType,
    setSubsidyAmt,
    setDayOfWeek,
    setMaxPriceLimit,
  }
}
